/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { ColorAliases } from 'styles/variables';
import { TimelineChart } from 'utilities/srs-timeline-library/timeline';
import { Bars } from 'utilities/srs-timeline-library/bar';
import { HistogramEndpointResponse } from 'srs-middleware';
import { isStacked, StackedHistogramEndpointResponse } from 'widgets/endpoints';
import { Dimensions } from 'interfaces';
import { Series } from 'd3';
import React, { useEffect, useState } from 'react';
import { TimelineTooltips } from './timelineTooltips';
import { useWidgetControlsContext } from 'views/common/WidgetControls';

export const Timeline: React.FC<TimelineProps> = ({
	dimensions,
	data,
	stacked,
	getColorScale,
	stackOrder,
	cropEmptyExtents,
	units,
	hideLabels,
  xAxisLabel,
	yAxisLabel
}) => {
	const {dispatch:controlsDispatch,state:controlsState} = useWidgetControlsContext()
	// Keep legend up to date
	useEffect(()=>{
		if(stacked && controlsDispatch) {
			controlsDispatch({
				type:"setLegend",
				payload: {
					keys:(data as StackedHistogramEndpointResponse).keys,
					colorScale:getColorScale,
				}
			})
		}
	},[data,getColorScale,controlsDispatch,stacked])

	// Filter data based on legend
	const [filteredData, setFilteredData] = useState(data)
	useEffect(()=>{
		if(isStacked(data) && controlsState?.legend){
			// Reconstruct data without disabled data
			const newFilteredData = {
				...data,
				keys:data.keys.filter((k)=>controlsState.legend.keyValues[k])
			}
			setFilteredData(newFilteredData)
		}
	},[controlsState,data])
	
	return (
		<div css={styles.wrapper}>
			<TimelineChart
				srsHistogramData={filteredData}
				dimensions={{ height: dimensions.height, width: dimensions.width }}
				margin={{ bottom: 14, left: 15 }}
				stacked={stacked}
				colorScale={getColorScale}
				stackOrder={stackOrder}
				cropEmptyExtents={cropEmptyExtents}
				hideLabels={hideLabels}
				xAxisLabel={xAxisLabel}
				yAxisLabel={yAxisLabel}
				legend={controlsState?.legend ? true : false}
			>
				<Bars />
				<TimelineTooltips units={units} />
			</TimelineChart>
		</div>
	);
};

interface TimelineProps {
	data: HistogramEndpointResponse | StackedHistogramEndpointResponse;
	dimensions: Dimensions;
	stacked?: boolean;
	getColorScale?: any;
	stackOrder?: StackOrder;
	cropEmptyExtents?: boolean;
	units?: string;
	hideLabels?: boolean;
	xAxisLabel?: string;
	yAxisLabel?: string;
}

export type StackOrder = (stack: Series<any, any>) => Iterable<number>;

const styles = {
	wrapper: css`
		//background-image: radial-gradient(circle, ${ColorAliases.PT_DIVIDER_BLACK} 1px, transparent 1px);
		//background-size: 5px 50px; // TODO: shouldn't these be spaced relative to the labels?
		display: flex;
		justify-content: space-between;
	`,
	labels: css`
		font-family: LibreFranklin, sans-serif;
		font-size: 12px;
		fill: ${ColorAliases.PT_TEXT_COLOR_MUTED};
		dominant-baseline: hanging;
	`,
};
